<template>
    <glass-panel
        radius="lg"
        class="analytics-activities pa-6"
    >
        <div class="d-flex align-center justify-space-between flex-wrap mb-5" style="gap:12px">
            <div>
                <eyebrow primary class="mb-1">
                    Conversões
                </eyebrow>
                <h3 class="analytics-activities__title">
                    Ações dos visitantes
                </h3>
            </div>

            <div class="analytics-activities__total">
                <span class="analytics-activities__total-num">{{ totalLabel }}</span>
                <span class="analytics-activities__total-label">conversões no total</span>
            </div>
        </div>

        <div class="analytics-activities__grid">
            <div
                v-for="card in cards"
                :key="card.key"
                class="analytics-activities__card"
                :class="`is-${card.tone}`"
            >
                <div class="analytics-activities__icon">
                    <v-icon
                        size="22"
                        :color="card.iconColor"
                    >
                        {{ card.icon }}
                    </v-icon>
                </div>
                <div class="analytics-activities__value">
                    {{ card.valueLabel }}
                </div>
                <div class="analytics-activities__label">
                    {{ card.label }}
                </div>
            </div>
        </div>
    </glass-panel>
</template>

<script>
const ACTIVITIES = [
    { key: 'FORM_SUBMITS', label: 'Formulários enviados', icon: 'mdi-form-textbox', tone: 'primary', iconColor: 'primary' },
    { key: 'CLICK_TO_CALLS', label: 'Cliques para ligar', icon: 'mdi-phone-outline', tone: 'secondary', iconColor: 'secondary' },
    { key: 'CLICK_TO_EMAILS', label: 'Cliques em e-mail', icon: 'mdi-email-outline', tone: 'tertiary', iconColor: 'tertiary' },
    { key: 'CLICK_TO_MAPS', label: 'Cliques no mapa', icon: 'mdi-map-marker-outline', tone: 'fixed', iconColor: 'primary' },
]

const formatNumber = (n) => {
    if (n == null || isNaN(n)) return '—'
    return Number(n).toLocaleString('pt-BR')
}

export default {
    name: 'AnalyticsActivities',
    props: {
        activities: {
            type: Object,
            default: () => ({}),
        },
    },
    computed: {
        cards() {
            return ACTIVITIES.map(a => {
                const raw = this.activities?.[a.key]
                return {
                    ...a,
                    rawValue: raw,
                    valueLabel: (raw === 0 || raw == null) ? '—' : formatNumber(raw),
                }
            })
        },
        total() {
            return ACTIVITIES.reduce((sum, a) => sum + (Number(this.activities?.[a.key]) || 0), 0)
        },
        totalLabel() {
            return formatNumber(this.total)
        },
    },
}
</script>

<style scoped>
.analytics-activities__title {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.25rem;
    letter-spacing: -0.01em;
    color: var(--color-dark-violet);
    margin: 0;
}

html.theme-dark .analytics-activities__title {
    color: var(--color-on-surface);
}

.analytics-activities__total {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    text-align: right;
}

.analytics-activities__total-num {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.5rem;
    line-height: 1;
    color: var(--color-primary);
    letter-spacing: -0.01em;
}

.analytics-activities__total-label {
    font-family: var(--font-body);
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-on-surface-variant);
    margin-top: 4px;
}

.analytics-activities__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

@media (min-width: 768px) {
    .analytics-activities__grid {
        grid-template-columns: repeat(4, 1fr);
    }
}

.analytics-activities__card {
    padding: 18px 16px;
    border-radius: var(--radius-md);
    background: var(--glass-soft-bg);
    border: 1px solid rgba(255, 255, 255, 0.45);
    transition: transform 240ms ease, box-shadow 240ms ease, border-color 240ms ease;
    position: relative;
    overflow: hidden;
}

html.theme-dark .analytics-activities__card {
    border-color: rgba(255, 255, 255, 0.08);
}

.analytics-activities__card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--card-tone-start, rgba(112, 42, 225, 0.06)), transparent 70%);
    pointer-events: none;
}

.analytics-activities__card.is-primary { --card-tone-start: rgba(112, 42, 225, 0.1); }
.analytics-activities__card.is-secondary { --card-tone-start: rgba(100, 72, 178, 0.1); }
.analytics-activities__card.is-tertiary { --card-tone-start: rgba(95, 82, 146, 0.1); }
.analytics-activities__card.is-fixed { --card-tone-start: rgba(178, 140, 255, 0.18); }

.analytics-activities__card:hover {
    transform: translateY(-3px);
    box-shadow: var(--shadow-glass-hover);
    border-color: rgba(112, 42, 225, 0.25);
}

.analytics-activities__icon {
    width: 36px;
    height: 36px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    position: relative;
    z-index: 1;
}

html.theme-dark .analytics-activities__icon {
    background: rgba(15, 25, 48, 0.7);
}

.analytics-activities__value {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.625rem;
    line-height: 1;
    letter-spacing: -0.01em;
    color: var(--color-dark-violet);
    margin-bottom: 4px;
    position: relative;
    z-index: 1;
}

html.theme-dark .analytics-activities__value {
    color: var(--color-on-surface);
}

.analytics-activities__label {
    font-family: var(--font-body);
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-on-surface-variant);
    line-height: 1.3;
    position: relative;
    z-index: 1;
}
</style>
